<template>
    <validation-observer ref="basicObserver" v-slot="{ invalid, errors }">

        <v-container fluid fill-height class="pa-4 pt-4">
            <v-row>

                <v-col cols="6">

                    <title-card title="Basic settings"
                                side-icon="mdi-clipboard-text-outline"
                                help="edit-project-basic-settings">

                        <!-- PROJECT NAME -->
                        <validation-provider
                                name="project name"
                                rules="min:3|max:150|required"
                                mode="lazy"
                                v-slot="{ errors }">

                            <v-text-field
                                    v-model="data.name"
                                    :error-messages="errors"
                                    label="Project name"
                                    @blur="emit"
                            ></v-text-field>

                        </validation-provider>

                        <v-alert v-show="showArchiveWarning" class="pt-2 pb-2 body-2" icon="mdi-alert" type="warning">
                            Archived projects do not accept any terminates or completes. It is recommended to close this
                            project first so that panelists can finish projects that are currently in progress.
                        </v-alert>

                        <!-- PROJECT STATUS -->
                        <v-select
                                label="Project status"
                                :items="statuses"
                                v-model="data.status"
                                item-text="name"
                                item-value="value"
                                @change="projectStatusChanged"
                        ></v-select>

                        <!-- PROJECT TYPE -->
                        <v-select
                                :disabled="!!projectId"
                                label="Project mode"
                                :items="projectTypes"
                                v-model="data.type"
                                item-text="name"
                                item-value="value"
                                @change="projectTypeChanged"
                        ></v-select>

                        <div class="d-flex justify-start">

                            <!-- TARGET COMPLETES -->
                            <validation-provider
                                    v-show="data.type==='auto' || data.type==='router'"
                                    name="target completes"
                                    rules="integer|min_value:1|max_value:10000|required"
                                    v-slot="{ errors }">

                                <v-text-field
                                        v-model="data.target_completes"
                                        :error-messages="errors"
                                        label="Target completes"
                                        hint="# of completed interviews"
                                        @change="emit"
                                ></v-text-field>

                            </validation-provider>

                            <!-- LOI -->
                            <validation-provider
                                    name="length of interview"
                                    rules="integer|min_value:1|max_value:120|required"
                                    v-slot="{ errors }">

                                <v-text-field
                                        v-model="data.loi"
                                        class="ml-4"
                                        :error-messages="errors"
                                        label="Length of interview"
                                        hint="Estimated # of minutes"
                                        @change="emit"
                                ></v-text-field>

                            </validation-provider>

                            <!-- ESTIMATED INCIDENCE -->
                            <validation-provider
                                    v-show="data.type==='auto'"
                                    name="Incidence rate"
                                    rules="required|integer|min_value:0|max_value:120"
                                    v-slot="{ errors }">

                                <v-text-field
                                        v-model="data.estimated_incidence_rate"
                                        class="ml-4"
                                        :error-messages="errors"
                                        label="Incidence rate"
                                        hint="Estimated %"
                                        @change="emit"
                                ></v-text-field>

                            </validation-provider>
                        </div>
                    </title-card>
                </v-col>

                <v-col cols="6">

                    <title-card title="Managers & Client"
                                side-icon="mdi-information-outline"
                                help="edit-project-managers-client">

                        <!-- PROJECT MANAGER -->
                        <user-picker
                                v-model="data.created_by"
                                :exclude="data.created_by"
                                @input="projectManagerSelected"
                                label="Project manager"
                                menu-title="Select project manager"
                                :roles="['Project Manager']"
                                :clearable="false"
                        ></user-picker>

                        <!-- ASSISTANT MANAGER -->
                        <user-picker
                                class="pt-5"
                                v-model="data.assistant_manager"
                                :exclude="data.assistant_manager"
                                @input="assistantManagerSelected"
                                label="Assistant manager"
                                menu-title="Select assistant manager"
                                :roles="['Project Manager']"
                        ></user-picker>

                        <!-- CLIENT -->
                        <client-picker
                                class="pt-5"
                                :selected-client="data.client"
                                :exclude="data.client"
                                @change="clientSelected"
                                label="Client selection"
                                menu-title="Select client">
                        </client-picker>
                    </title-card>
                </v-col>

                <!-- START DATE AND TIME -->
                <v-col cols="12" md="6">
                    <time-stamp-picker
                            color="grey darken-2"
                            help="edit-project-start-time"
                            toolbarColor="green darken-4"
                            icon="mdi-calendar-clock"
                            title="Start date and time"
                            :timestamp="data.start_date"
                            :max="data.end_date"
                            :error-messages="errorMessages.start_date ? errorMessages.start_date: []"
                            @changed="startDateChanged"
                    ></time-stamp-picker>

                </v-col>

                <!-- END DATE AND TIME -->
                <v-col cols="12" md="6">

                    <time-stamp-picker
                            color="grey darken-2"
                            toolbarColor="red darken-4"
                            icon="mdi-calendar-clock"
                            title="End date and time"
                            help="edit-project-end-time"
                            :timestamp="data.end_date"
                            :min="data.start_date"
                            :error-messages="errorMessages.end_date ? errorMessages.end_date: []"
                            @changed="endDateChanged"
                    ></time-stamp-picker>

                </v-col>

                <!-- EXTERNAL REFERENCES -->
                <v-col cols="12" md="6">

                    <title-card title="External references" side-icon="mdi-near-me"
                                help="edit-project-external-references">

                        <v-container class="pa-0">
                            <v-row dense>
                                <v-col cols="6">
                                    <v-text-field
                                            v-model="data.external_project_reference"
                                            hint="Project reference"
                                            persistent-hint
                                            dense
                                            @change="emit"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="6">
                                    <v-text-field
                                            v-model="data.external_billing_reference"
                                            hint="Billing reference"
                                            persistent-hint
                                            dense
                                            @change="emit"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </v-container>
                    </title-card>
                </v-col>

                <!-- EMAIL NOTIFICATIONS -->
                <v-col cols="12" md="6">
                    <title-card title="Notifications" side-icon="mdi-bell-outline" help="edit-project-notifications">

                        <v-switch
                                color="primary"
                                v-model="data.email_notifications_enabled"
                                label="Enable email notifications"
                                @change="emit"
                                hide-details
                                class="mt-2 mb-3"
                        ></v-switch>

                    </title-card>
                </v-col>

                <!-- SAMPLING PRIORITY -->
                <v-col cols="12" md="6" v-show="data.type==='auto'">

                    <title-card :hide-contents="data.type==='recontact' || data.type==='router'"
                                title="Sampling priority"
                                side-icon="mdi-priority-high"
                                help="edit-project-sampling-priority">

                        <template v-slot:overlay>
                            <v-alert type="info" class="body-2" color="grey darken-2 dense" style="opacity: 0.8">
                                Not available in recontact mode
                            </v-alert>
                        </template>

                        <v-slider
                                class="pt-8"
                                v-model="data.sampling_priority"
                                hint="Sampling priority"
                                thumb-color="primary"
                                thumb-label="always"
                                persistent-hint
                                min="1"
                                max="10"
                                append-icon="mdi-plus"
                                prepend-icon="mdi-minus"
                                ticks
                                step="1"
                                @change="emit"
                        ></v-slider>
                    </title-card>
                </v-col>

                <!-- CATEGORIES -->
                <v-col cols="12" md="6">

                    <title-card title="Assign categories" side-icon="mdi-shape-outline" help="edit-project-categories">

                        <category-picker
                                class="pb-4"
                                label="Click to select categories"
                                v-model="data.categories"
                                @input="categoriesChanged"
                        ></category-picker>

                    </title-card>
                </v-col>

                <!-- DEVICES -->
                <v-col cols="12" md="6">

                    <title-card title="Device compatibility" side-icon="mdi-cellphone-link"
                                help="edit-project-device-compatibility">

                        <device-compatibility-selector :data="data"
                                                       @change="devicesChanged"
                        ></device-compatibility-selector>

                    </title-card>

                </v-col>

                <v-col cols="12" md="6">

                    <!-- ROUTING BOX -->
                    <title-card
                            v-if="hasFeature('project_routing') && data.type==='auto' && $store.state.PanelSettings.settings.routing_enabled"
                            title="Routing"
                            side-icon="mdi-routes"
                            help="edit-project-route-in">

                        <!-- ROUTE IN ENABLED -->
                        <v-switch
                                color="primary"
                                v-model="data.route_in_enabled"
                                label="Enable routing in"
                                @change="emit"
                                hide-details
                                class="mt-0"
                        ></v-switch>

                        <!-- ROUTE OUT ENABLED -->
                        <v-switch
                                color="primary"
                                v-model="data.route_out_enabled"
                                label="Enable routing out"
                                @change="emit"
                                hide-details
                        ></v-switch>

                    </title-card>
                </v-col>

                <v-col cols="12" v-if="debug">
                    <json :data="data"></json>
                </v-col>
            </v-row>

        </v-container>
    </validation-observer>
</template>

<script>

import Json from 'vue-json-pretty';
import TimeStampPicker from '../../../../../components/UI/TimeStampPicker';
import UserPicker from '../../../../../components/UI/UserPicker';
import ClientPicker from '../../../../../components/UI/ClientPicker';
import CategoryPicker from '../../CategoryPicker';
import TitleCard from "../../../../../components/UI/TitleCard";
import DeviceCompatibilitySelector from "../../DeviceCompatibilitySelector";

export default {

    components: {
        TimeStampPicker,
        CategoryPicker,
        UserPicker,
        ClientPicker,
        Json,
        TitleCard,
        DeviceCompatibilitySelector
    },

    props: {

        projectName: {
            required: false,
            type: String,
            default: null,
        },
        projectId: {
            required: false,
            type: Number,
            default: null,
        },

        loi: {
            required: false,
            type: Number | String,
            default: 15
        },
        createdBy: {
            required: false,
            type: Object,
            default: () => {
            }
        },
        status: {
            required: false,
            type: String,
            default: 'offline'
        },
        type: {
            required: false,
            type: String,
            default: 'auto'
        },
        startDate: {
            required: false,
            type: String,
            default: null,
        },
        endDate: {
            required: false,
            type: String,
            default: null,
        },
        targetCompletes: {
            required: false,
            type: Number | String,
            default: null,
        },
        samplingPriority: {
            required: true,
            type: Number
        },
        categories: {
            required: false,
            type: Array,
            default: () => {
            }
        },
        assistantManager: {
            required: false,
            type: Object,
            default: () => {
            }
        },
        client: {
            required: false,
            type: Object,
            default: () => {
            }
        },
        emailNotificationsEnabled: {
            required: false,
            type: Boolean,
            default: true,
        },
        mobileFriendly: {
            required: false,
            type: Boolean,
            default: true,
        },
        tabletFriendly: {
            required: false,
            type: Boolean,
            default: true,
        },
        desktopFriendly: {
            required: false,
            type: Boolean,
            default: true,
        },
        errorMessages: {
            required: false,
            type: Object,
            default: () => {
                return {}
            }
        },
        loading: {
            required: true,
            type: Boolean,
            default: null,
        },
        routeInEnabled: {
            required: true,
            type: Boolean,
            default: true,
        },
        routeOutEnabled: {
            required: true,
            type: Boolean,
            default: true,
        },
        estimatedIncidenceRate: {
            type: Number | String,
            required: true,
            default: 20
        },
        externalProjectReference: {
            required: true,
            type: String | null,
            default: null,
        },
        externalBillingReference: {
            required: true,
            type: String | null,
            default: null,
        }
    },

    watch: {
        projectName(value) {
            this.data.name = value;
        },
        loi(value) {
            this.data.loi = value;
        },
        createdBy(value) {
            this.data.created_by = value;
        },
        status(value) {
            this.data.status = value;
        },
        startDate(value) {
            this.data.start_date = value;
        },
        endDate(value) {
            this.data.end_date = value;
        },
        type(value) {
            this.data.type = value;
        },
        targetCompletes(value) {
            this.data.target_completes = value;
        },
        samplingPriority(value) {
            this.data.sampling_priority = value;
        },
        categories(value) {
            this.data.categories = value;
        },
        assistantManager(value) {
            this.data.assistant_manager = value;
        },
        client(value) {
            this.data.client = value;
        },
        emailNotificationsEnabled(value) {
            this.data.email_notifications_enabled = value;
        },
        mobileFriendly(value) {
            this.data.mobile_friendly = value;
        },
        tabletFriendly(value) {
            this.data.tablet_friendly = value;
        },
        desktopFriendly(value) {
            this.data.desktop_friendly = value;
        },
        routeInEnabled(value) {
            this.data.route_in_enabled = value;
        },
        estimatedIncidenceRate(value) {
            this.data.estimated_incidence_rate = value;
        },
        externalProjectReference(value) {
            this.data.external_project_reference = value;
        },
        externalBillingReference(value) {
            this.data.external_billing_reference = value;
        },
    },

    data() {
        return {
            debug: false,

            data: {
                name: null,
                status: null,
                type: null,
                start_date: null,
                end_date: null,
                created_by: null,
                target_completes: null,
                estimated_completion_rate: null,
                loi: null,
                sampling_priority: null,
                categories: null,
                assistant_manager: null,
                client: null,
                email_notifications_enabled: null,
                mobile_friendly: null,
                tablet_friendly: null,
                desktop_friendly: null,
                estimated_incidence_rate: null,
                external_project_reference: null,
                external_billing_reference: null,
            },

            statuses: [
                {name: 'Online', value: 'online'},
                {name: 'Offline', value: 'offline'},
                {name: 'Closed', value: 'closed'},
                {name: 'Archived', value: 'archived'}
            ],

            projectTypes: [
                {name: 'Auto sampling', value: 'auto'},
                {name: 'Recontact', value: 'recontact'},
            ],

            previousStatus: null,
            showArchiveWarning: false,
        }
    },

    mounted() {
        this.data.loi = this.loi;
        this.data.created_by = this.createdBy;
        this.data.name = this.projectName;
        this.data.status = this.status;
        this.data.type = this.type;
        this.data.start_date = this.startDate;
        this.data.end_date = this.endDate;
        this.data.target_completes = this.targetCompletes;
        this.data.sampling_priority = this.samplingPriority;
        this.data.categories = this.categories;
        this.data.assistant_manager = this.assistantManager;
        this.data.client = this.client;
        this.data.email_notifications_enabled = this.emailNotificationsEnabled;
        this.data.mobile_friendly = this.mobileFriendly;
        this.data.tablet_friendly = this.tabletFriendly;
        this.data.desktop_friendly = this.desktopFriendly;
        this.data.route_in_enabled = this.routeInEnabled;
        this.data.route_out_enabled = this.routeOutEnabled;
        this.data.estimated_incidence_rate = this.estimatedIncidenceRate;
        this.data.external_project_reference = this.externalProjectReference;
        this.data.external_billing_reference = this.externalBillingReference;
        this.emit(false);

        if (this.hasFeature('project_router_mode')) {
            this.projectTypes.push({name: 'Router', value: 'router'});
        }
    },

    methods: {

        startDateChanged(value) {
            this.data.start_date = value;
            this.emit();
        },

        endDateChanged(value) {
            this.data.end_date = value;
            this.emit();
        },

        categoriesChanged(value) {
            this.data.categories = value;
            this.emit();
        },

        assistantManagerSelected(user) {
            this.data.assistant_manager = user;
            this.emit();
        },

        projectManagerSelected(user) {
            this.data.created_by = user;
            this.emit();
        },

        clientSelected(client) {
            this.data.client = client;
            this.emit();
        },

        devicesChanged(devices) {
            this.data = Object.assign(this.data, devices);
            this.emit();
        },

        projectTypeChanged(type) {
            this.data.type = type;

            if (this.data.type === 'router') {
                this.data.route_in_enabled = false;
            }
            this.emit();
        },

        projectStatusChanged(status) {
            if (status === "archived" && this.previousStatus === 'online') {
                this.showArchiveWarning = true;
            } else {
                this.showArchiveWarning = false;
            }
            this.previousStatus = status;
            this.emit();
        },

        emit(validate = true) {

            if (!validate) {
                this.$emit('change', Object.assign({}, this.data));

            } else {

                if (!this.loading && this.$refs.basicObserver) {

                    this.$refs.basicObserver.validate()
                            .then(isValid => {

                                if (isValid) {
                                    this.$emit('error', {name: 'tab1', error: false});
                                    this.$emit('change', Object.assign({}, this.data));
                                } else {
                                    this.$emit('error', {name: 'tab1', error: true});
                                }
                            });
                }
            }
        },
    }
}
</script>
